import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Search, MapPin, Home, DollarSign, Bed, Bath, Maximize, Plus, Eye, MessageSquare,
    Filter, ArrowUpDown, Heart, ChevronLeft, ChevronRight, X, Send, CheckCircle
} from 'lucide-react';

const PROPERTY_TYPES = ['single_family', 'condo', 'townhouse', 'multi_family'];
const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
];

export default function MarketplacePage() {
    const { user } = useAuth();
    const [listings, setListings] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ min_price: '', max_price: '', city: '', property_type: '', bedrooms: '', sort: 'newest' });
    const [showFilters, setShowFilters] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [myListings, setMyListings] = useState([]);
    const [showCreate, setShowCreate] = useState(false);

    const loadListings = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('sort', filters.sort);
            if (filters.min_price) params.set('min_price', filters.min_price);
            if (filters.max_price) params.set('max_price', filters.max_price);
            if (filters.city) params.set('city', filters.city);
            if (filters.property_type) params.set('property_type', filters.property_type);
            if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
            const { data } = await API.get(`/marketplace/listings?${params.toString()}`);
            setListings(data.listings);
            setTotal(data.total);
            setPages(data.pages);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [page, filters]);

    const loadMyListings = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await API.get('/marketplace/my-listings');
            setMyListings(data);
        } catch (err) { /* not logged in */ }
    }, [user]);

    useEffect(() => { loadListings(); }, [loadListings]);
    useEffect(() => { loadMyListings(); }, [loadMyListings]);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="marketplace-page">
            <Navbar variant="light" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']" data-testid="marketplace-title">Day 91 Marketplace</h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            {total} active properties — homes that didn't sell through traditional channels
                        </p>
                    </div>
                    {user && (
                        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm" data-testid="create-listing-btn">
                            <Plus className="w-4 h-4 mr-2" /> List Property
                        </Button>
                    )}
                </div>

                {/* YouTube Explainer */}
                <div className="bg-white rounded-xl border border-zinc-200 shadow-sm mb-6 overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                        <div className="md:col-span-2 aspect-video">
                            <video
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_explainer_final.mp4?v=2`}
                                title="VisionAire Realty Services - How It Works"
                                controls
                                muted
                                
                                playsInline
                                poster={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/visionaire_poster.jpg?v=2`}
                                className="w-full h-full object-cover"
                                data-testid="marketplace-video"
                            />
                        </div>
                        <div className="p-5 flex flex-col justify-center">
                            <div className="text-xs tracking-[0.15em] uppercase font-bold text-amber-600 mb-2">How It Works</div>
                            <h3 className="text-lg font-semibold text-zinc-900 font-['Outfit'] mb-2">The Day 91 Concept</h3>
                            <p className="text-sm text-zinc-600 leading-relaxed">
                                When a home doesn't sell after 90 days, VisionAire steps in. We connect these properties with qualified buyers through owner financing — creating opportunities where traditional methods failed.
                            </p>
                            <div className="mt-3 text-xs text-zinc-500">
                                Sellers submit properties. Agents enroll listings. Operators bring buyers. Everyone wins.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm mb-6">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-48">
                            <div className="relative">
                                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                                    placeholder="Search by city..." className="pl-9 border-zinc-200 h-9 text-sm" data-testid="marketplace-search" />
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="border-zinc-200 text-xs">
                            <Filter className="w-3 h-3 mr-1" /> Filters
                        </Button>
                        <select value={filters.sort} onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))}
                            className="border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="marketplace-sort">
                            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <Button size="sm" onClick={() => { setPage(1); loadListings(); }} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg" data-testid="apply-filters-btn">
                            <Search className="w-3 h-3 mr-1" /> Search
                        </Button>
                    </div>
                    {showFilters && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-zinc-200">
                            <div><Label className="text-xs text-zinc-500">Min Price</Label><Input type="number" value={filters.min_price} onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))} placeholder="0" className="border-zinc-200 h-8 text-sm" /></div>
                            <div><Label className="text-xs text-zinc-500">Max Price</Label><Input type="number" value={filters.max_price} onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))} placeholder="Any" className="border-zinc-200 h-8 text-sm" /></div>
                            <div><Label className="text-xs text-zinc-500">Type</Label>
                                <select value={filters.property_type} onChange={e => setFilters(f => ({ ...f, property_type: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white">
                                    <option value="">All</option>
                                    {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                                </select>
                            </div>
                            <div><Label className="text-xs text-zinc-500">Min Beds</Label><Input type="number" value={filters.bedrooms} onChange={e => setFilters(f => ({ ...f, bedrooms: e.target.value }))} placeholder="Any" className="border-zinc-200 h-8 text-sm" /></div>
                        </div>
                    )}
                </div>

                {/* Listings Grid */}
                {loading ? (
                    <div className="text-center py-12"><div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : listings.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-zinc-200">
                        <Home className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                        <p className="text-sm text-zinc-400">No properties found. Try adjusting your filters.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {listings.map(l => (
                            <ListingCard key={l.id} listing={l} onSelect={() => setSelectedListing(l)} user={user} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-zinc-200">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-zinc-600">Page {page} of {pages}</span>
                        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-zinc-200">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                )}

                {/* My Listings (if seller/admin) */}
                {user && myListings.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">My Listings</h2>
                        <div className="space-y-3">
                            {myListings.map(l => (
                                <div key={l.id} className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium text-zinc-900">{l.property_address}</div>
                                        <div className="text-xs text-zinc-500">${(l.asking_price || 0).toLocaleString()} | {l.bedrooms}bd/{l.bathrooms}ba | {(l.sqft || 0).toLocaleString()} sqft</div>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                                        <span><Eye className="w-3 h-3 inline mr-0.5" />{l.views || 0}</span>
                                        <span><MessageSquare className="w-3 h-3 inline mr-0.5" />{l.inquiries || 0}</span>
                                        <Badge className={l.listing_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}>{l.listing_status}</Badge>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedListing && <ListingModal listing={selectedListing} onClose={() => setSelectedListing(null)} user={user} />}

            {/* Create Listing Modal */}
            {showCreate && <CreateListingModal onClose={() => { setShowCreate(false); loadListings(); loadMyListings(); }} />}
        </div>
    );
}

function ListingCard({ listing, onSelect, user }) {
    const l = listing;
    const hasImages = l.image_urls && l.image_urls.length > 0;
    return (
        <div onClick={onSelect}
            className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md hover:border-zinc-300 transition-all group"
            data-testid={`listing-card-${l.id}`}>
            <div className="h-44 bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center relative overflow-hidden">
                {hasImages ? (
                    <img src={l.image_urls[0]} alt={l.property_address} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                    <Home className="w-12 h-12 text-zinc-300" />
                )}
                {l.financing_available && (
                    <Badge className="absolute top-3 left-3 bg-emerald-700 text-white text-[10px]">Owner Finance Available</Badge>
                )}
                <div className="absolute top-3 right-3 flex gap-1.5">
                    {hasImages && l.image_urls.length > 1 && (
                        <span className="text-[10px] text-zinc-700 bg-white/90 px-2 py-0.5 rounded-full font-medium">{l.image_urls.length} photos</span>
                    )}
                    <span className="text-[10px] text-zinc-400 bg-white/80 px-2 py-0.5 rounded-full">
                        <Eye className="w-2.5 h-2.5 inline mr-0.5" />{l.views || 0}
                    </span>
                </div>
                {l.current_dom > 0 && (
                    <div className="absolute bottom-3 left-3 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-medium">
                        {l.current_dom} DOM
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="text-lg font-semibold text-emerald-700 font-['Outfit'] mb-1">${(l.asking_price || 0).toLocaleString()}</div>
                <div className="text-sm text-zinc-900 mb-1 truncate">{l.property_address}</div>
                {(l.city || l.state) && <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{[l.city, l.state].filter(Boolean).join(', ')}</div>}
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                    {l.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{l.bedrooms} bd</span>}
                    {l.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{l.bathrooms} ba</span>}
                    {l.sqft > 0 && <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" />{l.sqft.toLocaleString()} sqft</span>}
                </div>
                {l.property_type && l.property_type !== 'single_family' && (
                    <div className="mt-2"><Badge className="bg-zinc-100 text-zinc-500 text-[10px]">{l.property_type.replace('_', ' ')}</Badge></div>
                )}
            </div>
        </div>
    );
}

function ListingModal({ listing, onClose, user }) {
    const l = listing;
    const [inquiryMsg, setInquiryMsg] = useState('');
    const [sent, setSent] = useState(false);
    const [currentImg, setCurrentImg] = useState(0);
    const hasImages = l.image_urls && l.image_urls.length > 0;
    const imageCount = hasImages ? l.image_urls.length : 0;

    const sendInquiry = async () => {
        if (!user) { alert('Please login to inquire'); return; }
        try {
            await API.post('/marketplace/inquire', { listing_id: l.id, message: inquiryMsg });
            setSent(true);
        } catch (err) { alert(err.response?.data?.detail || 'Failed to send'); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="listing-modal">
                {/* Image Gallery */}
                <div className="relative h-64 sm:h-80 bg-gradient-to-br from-zinc-100 to-zinc-200 rounded-t-2xl overflow-hidden">
                    {hasImages ? (
                        <>
                            <img src={l.image_urls[currentImg]} alt={`${l.property_address} - Photo ${currentImg + 1}`} className="w-full h-full object-cover" />
                            {imageCount > 1 && (
                                <>
                                    <button onClick={(e) => { e.stopPropagation(); setCurrentImg(i => (i - 1 + imageCount) % imageCount); }}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                                        data-testid="gallery-prev">
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setCurrentImg(i => (i + 1) % imageCount); }}
                                        className="absolute right-14 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                                        data-testid="gallery-next">
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                        {l.image_urls.map((_, i) => (
                                            <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentImg(i); }}
                                                className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-white scale-125' : 'bg-white/50'}`} />
                                        ))}
                                    </div>
                                    <div className="absolute top-3 left-3 text-xs text-white bg-black/50 px-2.5 py-1 rounded-full">
                                        {currentImg + 1} / {imageCount}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center"><Home className="w-16 h-16 text-zinc-300" /></div>
                    )}
                    <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors" data-testid="close-modal">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Thumbnail strip */}
                {imageCount > 1 && (
                    <div className="flex gap-2 px-6 pt-3 overflow-x-auto" data-testid="gallery-thumbnails">
                        {l.image_urls.map((url, i) => (
                            <button key={i} onClick={() => setCurrentImg(i)}
                                className={`w-16 h-12 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${i === currentImg ? 'border-emerald-500 ring-1 ring-emerald-300' : 'border-zinc-200 opacity-60 hover:opacity-100'}`}>
                                <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}

                <div className="p-6">
                    {/* Price & Address */}
                    <div className="mb-4">
                        <div className="text-2xl font-bold text-emerald-700 font-['Outfit'] mb-1">${(l.asking_price || 0).toLocaleString()}</div>
                        <div className="text-base font-medium text-zinc-900 mb-1">{l.property_address}</div>
                        {(l.city || l.state) && <div className="text-sm text-zinc-500"><MapPin className="w-3.5 h-3.5 inline mr-1" />{[l.city, l.state, l.zip_code].filter(Boolean).join(', ')}</div>}
                    </div>

                    {/* Key Specs Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="property-specs">
                        {l.bedrooms > 0 && (
                            <div className="bg-zinc-50 rounded-lg p-3 text-center border border-zinc-200">
                                <Bed className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                                <div className="text-lg font-semibold text-zinc-900">{l.bedrooms}</div>
                                <div className="text-xs text-zinc-500">Bedrooms</div>
                            </div>
                        )}
                        {l.bathrooms > 0 && (
                            <div className="bg-zinc-50 rounded-lg p-3 text-center border border-zinc-200">
                                <Bath className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                                <div className="text-lg font-semibold text-zinc-900">{l.bathrooms}</div>
                                <div className="text-xs text-zinc-500">Bathrooms</div>
                            </div>
                        )}
                        {l.sqft > 0 && (
                            <div className="bg-zinc-50 rounded-lg p-3 text-center border border-zinc-200">
                                <Maximize className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                                <div className="text-lg font-semibold text-zinc-900">{l.sqft.toLocaleString()}</div>
                                <div className="text-xs text-zinc-500">Sq. Ft.</div>
                            </div>
                        )}
                        {l.year_built > 0 && (
                            <div className="bg-zinc-50 rounded-lg p-3 text-center border border-zinc-200">
                                <Home className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                                <div className="text-lg font-semibold text-zinc-900">{l.year_built}</div>
                                <div className="text-xs text-zinc-500">Year Built</div>
                            </div>
                        )}
                    </div>

                    {/* Property Details Table */}
                    <div className="mb-5">
                        <h4 className="text-sm font-semibold text-zinc-800 mb-2">Property Details</h4>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                            {l.property_type && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">Type</span><span className="text-zinc-900 font-medium">{l.property_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span></div>}
                            {l.lot_size && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">Lot Size</span><span className="text-zinc-900 font-medium">{l.lot_size}</span></div>}
                            {l.condition && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">Condition</span><span className="text-zinc-900 font-medium">{l.condition}</span></div>}
                            {l.parking && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">Parking</span><span className="text-zinc-900 font-medium">{l.parking}</span></div>}
                            {l.hoa_fee != null && l.hoa_fee > 0 && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">HOA Fee</span><span className="text-zinc-900 font-medium">${l.hoa_fee}/mo</span></div>}
                            {l.current_dom > 0 && <div className="flex justify-between py-1 border-b border-zinc-100"><span className="text-zinc-500">Days on Market</span><span className="text-amber-600 font-semibold">{l.current_dom} days</span></div>}
                        </div>
                    </div>

                    {/* Description */}
                    {l.description && (
                        <div className="mb-5">
                            <h4 className="text-sm font-semibold text-zinc-800 mb-1">Description</h4>
                            <p className="text-sm text-zinc-600 leading-relaxed">{l.description}</p>
                        </div>
                    )}

                    {/* Features */}
                    {l.features?.length > 0 && (
                        <div className="mb-5">
                            <h4 className="text-sm font-semibold text-zinc-800 mb-2">Features & Amenities</h4>
                            <div className="flex flex-wrap gap-1.5">
                                {l.features.map((f, i) => <Badge key={i} className="bg-zinc-100 text-zinc-600 text-xs">{f}</Badge>)}
                            </div>
                        </div>
                    )}

                    {/* Owner Finance Terms */}
                    {l.financing_available && l.owner_finance_terms && (
                        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 mb-5" data-testid="finance-terms">
                            <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-4 h-4 text-emerald-700" />
                                <span className="text-sm font-semibold text-emerald-800">Owner Financing Available</span>
                            </div>
                            <p className="text-sm text-emerald-700">{l.owner_finance_terms}</p>
                        </div>
                    )}

                    {/* Inquiry Form */}
                    {user && !sent && (
                        <div className="border-t border-zinc-200 pt-4">
                            <div className="text-sm font-medium text-zinc-900 mb-2">Interested? Send an Inquiry</div>
                            <Textarea value={inquiryMsg} onChange={e => setInquiryMsg(e.target.value)}
                                placeholder="I'm interested in this property and would like to learn more about the financing terms..."
                                className="border-zinc-200 text-sm mb-2 min-h-16" data-testid="inquiry-message" />
                            <Button onClick={sendInquiry} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm" data-testid="send-inquiry-btn">
                                <Send className="w-4 h-4 mr-2" /> Send Inquiry
                            </Button>
                        </div>
                    )}
                    {sent && (
                        <div className="border-t border-zinc-200 pt-4 text-center">
                            <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                            <div className="text-emerald-600 text-sm font-medium">Inquiry sent! The seller will be notified.</div>
                        </div>
                    )}
                    {!user && (
                        <div className="border-t border-zinc-200 pt-4 text-center">
                            <p className="text-sm text-zinc-500">Please <a href="/login" className="text-emerald-600 font-medium hover:underline">log in</a> to send an inquiry.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CreateListingModal({ onClose }) {
    const [form, setForm] = useState({
        property_address: '', city: '', state: '', zip_code: '', property_type: 'single_family',
        bedrooms: 3, bathrooms: 2, sqft: 0, year_built: 0, asking_price: 0,
        days_on_market: 0, description: '', features: [], financing_available: true,
        owner_finance_terms: '', image_urls: [],
    });
    const [featureInput, setFeatureInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const addFeature = () => {
        if (featureInput.trim()) {
            setForm(f => ({ ...f, features: [...f.features, featureInput.trim()] }));
            setFeatureInput('');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await API.post('/marketplace/upload-image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm(f => ({ ...f, image_urls: [...f.image_urls, data.url] }));
        } catch (err) {
            alert(err.response?.data?.detail || 'Upload failed');
        } finally { setUploading(false); }
    };

    const submit = async () => {
        setLoading(true);
        try {
            await API.post('/marketplace/my-listings', form);
            onClose();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create');
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-listing-modal">
                <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit'] mb-4">List Your Property</h2>
                <div className="space-y-4">
                    {/* Image Upload */}
                    <div>
                        <Label className="text-xs font-medium">Property Photos</Label>
                        <div className="flex items-center gap-3 mt-1">
                            {form.image_urls.map((url, i) => (
                                <div key={i} className="w-16 h-16 rounded-lg overflow-hidden border border-zinc-200 relative">
                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                    <button onClick={() => setForm(f => ({ ...f, image_urls: f.image_urls.filter((_, j) => j !== i) }))}
                                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px]">x</button>
                                </div>
                            ))}
                            <label className="w-16 h-16 rounded-lg border-2 border-dashed border-zinc-300 flex items-center justify-center cursor-pointer hover:border-emerald-400 transition-colors"
                                data-testid="listing-image-upload">
                                {uploading ? <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /> : <Plus className="w-5 h-5 text-zinc-400" />}
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                            </label>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label className="text-xs">Property Address</Label><Input value={form.property_address} onChange={e => setForm(f => ({ ...f, property_address: e.target.value }))} className="border-zinc-200 h-9 text-sm" data-testid="listing-address" /></div>
                        <div><Label className="text-xs">City</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Asking Price ($)</Label><Input type="number" value={form.asking_price || ''} onChange={e => setForm(f => ({ ...f, asking_price: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" data-testid="listing-price" /></div>
                        <div><Label className="text-xs font-semibold text-amber-700">Days on Market</Label>
                            <Input type="number" value={form.days_on_market || ''} onChange={e => setForm(f => ({ ...f, days_on_market: parseInt(e.target.value) || 0 }))} className="border-amber-300 h-9 text-sm" data-testid="listing-dom" />
                        </div>
                        <div><Label className="text-xs">Type</Label>
                            <select value={form.property_type} onChange={e => setForm(f => ({ ...f, property_type: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white">
                                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                            </select>
                        </div>
                        <div><Label className="text-xs">Beds</Label><Input type="number" value={form.bedrooms} onChange={e => setForm(f => ({ ...f, bedrooms: parseInt(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Baths</Label><Input type="number" value={form.bathrooms} onChange={e => setForm(f => ({ ...f, bathrooms: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Sqft</Label><Input type="number" value={form.sqft || ''} onChange={e => setForm(f => ({ ...f, sqft: parseInt(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Year Built</Label><Input type="number" value={form.year_built || ''} onChange={e => setForm(f => ({ ...f, year_built: parseInt(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    </div>
                    <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="border-zinc-200 text-sm min-h-16" /></div>
                    <div>
                        <Label className="text-xs">Owner Finance Terms</Label>
                        <Textarea value={form.owner_finance_terms} onChange={e => setForm(f => ({ ...f, owner_finance_terms: e.target.value }))} placeholder="e.g., 10% down, 6% interest, 5yr balloon" className="border-zinc-200 text-sm min-h-12" />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex-1 flex gap-2">
                            <Input value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }} placeholder="Add feature..." className="border-zinc-200 h-8 text-sm" />
                            <Button size="sm" variant="outline" onClick={addFeature} className="text-xs border-zinc-200 h-8">Add</Button>
                        </div>
                    </div>
                    {form.features.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {form.features.map((f, i) => (
                                <Badge key={i} className="bg-zinc-100 text-zinc-600 text-xs cursor-pointer" onClick={() => setForm(fm => ({ ...fm, features: fm.features.filter((_, j) => j !== i) }))}>{f} <X className="w-2.5 h-2.5 ml-1" /></Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex gap-2 mt-6 pt-4 border-t border-zinc-200">
                    <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="submit-listing-btn">
                        {loading ? 'Creating...' : 'Publish Listing'}
                    </Button>
                    <Button variant="outline" onClick={onClose} className="border-zinc-200">Cancel</Button>
                </div>
            </div>
        </div>
    );
}
